import React, { useState, useEffect } from 'react';
import { Card, Form, Button, ListGroup, Alert } from 'react-bootstrap';
import { ref, get, set, push, remove, onValue } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';

import { Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';

const BikeConfigPage = () => {
  const { currentUser } = useAuth();
  const [existingBikeId, setExistingBikeId] = useState('');
  const [ownedBikeIds, setOwnedBikeIds] = useState<string[]>([]);
  const [alert, setAlert] = useState<string | null>(null);
  const [valveTypes, setValveTypes] = useState<{[key: string]: string}>({});
  const [newValveType, setNewValveType] = useState('');
  const [valveAlert, setValveAlert] = useState<{type: 'success' | 'danger', message: string} | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!currentUser) return;

    // Fetch valve types from database
    const valveTypesRef = ref(database, `user_settings/${currentUser.uid}/valve_types`);
    const unsubscribe = onValue(valveTypesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setValveTypes(data);
      } else {
        setValveTypes({});
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleAddValveType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !newValveType.trim()) return;

    try {
      const valveTypesRef = ref(database, `user_settings/${currentUser.uid}/valve_types`);
      await push(valveTypesRef, newValveType.trim());
      setNewValveType('');
      setValveAlert({
        type: 'success',
        message: 'Ventil-Typ erfolgreich hinzugefügt!'
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setValveAlert(null);
      }, 3000);
    } catch (error) {
      console.error('Error adding valve type:', error);
      setValveAlert({
        type: 'danger',
        message: 'Fehler beim Hinzufügen des Ventil-Typs.'
      });
    }
  };

  const handleRemoveValveType = async (valveTypeId: string) => {
    if (!currentUser || !window.confirm('Sind Sie sicher, dass Sie diesen Ventil-Typ löschen möchten?')) return;

    try {
      const valveTypeRef = ref(database, `user_settings/${currentUser.uid}/valve_types/${valveTypeId}`);
      await remove(valveTypeRef);
      setValveAlert({
        type: 'success',
        message: 'Ventil-Typ erfolgreich gelöscht!'
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setValveAlert(null);
      }, 3000);
    } catch (error) {
      console.error('Error removing valve type:', error);
      setValveAlert({
        type: 'danger',
        message: 'Fehler beim Löschen des Ventil-Typs.'
      });
    }
  };

  const handleAddExistingBike = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !existingBikeId.trim()) return;
    try {
      // Check if bike exists
      const bikeRef = ref(database, `bikes/${existingBikeId}`);
      const bikeSnapshot = await get(bikeRef);
      if (!bikeSnapshot.exists()) {
        setAlert('Velo mit dieser ID wurde nicht gefunden.');
        return;
      }
      // Check if bike is already in owned list
      if (ownedBikeIds.includes(existingBikeId)) {
        setAlert('Dieses Velo ist bereits in Ihrer Liste.');
        return;
      }
      // Add to owned bikes
      const updatedOwnedBikes = [...ownedBikeIds, existingBikeId];
      const ownedBikesRef = ref(database, `user_settings/${currentUser.uid}/ownedBikes`);
      await set(ownedBikesRef, updatedOwnedBikes);
      setExistingBikeId('');
      setAlert('Velo erfolgreich hinzugefügt!');
      setOwnedBikeIds(updatedOwnedBikes);
    } catch (error) {
      setAlert('Fehler beim Hinzufügen des Velos.');
    }
  };

  return (
    <div className="container">
      <h1 className="mb-3">Velo hinzufügen</h1>
      {/* Bike section tabs, shown only on /bike, /bike/maintenance, /bike/settings */}
      {['/bike', '/bike/maintenance', '/bike/settings'].includes(location.pathname) && (
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link as={Link} to="/bike" active={location.pathname === '/bike'}>
              Velos
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={Link} to="/bike/maintenance" active={location.pathname === '/bike/maintenance'}>
              Wartung
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={Link} to="/bike/settings" active={location.pathname === '/bike/settings'}>
              Konfiguration
            </Nav.Link>
          </Nav.Item>
        </Nav>
      )}
      {/* Inline BikeAddExisting logic and UI */}
      <Card>
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>
            Existierendes Velo hinzufügen
          </Card.Title>
          <Form onSubmit={handleAddExistingBike}>
            <Form.Group className="mb-3">
              <Form.Label>Velo ID</Form.Label>
              <Form.Control
                type="text"
                value={existingBikeId}
                onChange={e => setExistingBikeId(e.target.value)}
                required
              />
            </Form.Group>
            <Button type="submit" variant="primary">
              Speichern
            </Button>
          </Form>
          {alert && <div className="mt-3 text-danger">{alert}</div>}
        </Card.Body>
      </Card>

      {/* Valve Types Management Card */}
      <Card className="mt-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>
            Ventil-Typen
          </Card.Title>
          
          {valveAlert && (
            <Alert variant={valveAlert.type} className="mb-3">
              {valveAlert.message}
            </Alert>
          )}

          {/* Add new valve type form */}
          <Form onSubmit={handleAddValveType} className="mb-3">
            <Form.Group className="mb-3">
              <Form.Label>Neuer Ventil-Typ</Form.Label>
              <Form.Control
                type="text"
                value={newValveType}
                onChange={e => setNewValveType(e.target.value)}
                placeholder="z.B. Presta, Schrader, Dunlop"
                required
              />
            </Form.Group>
            <Button type="submit" variant="primary">
              Hinzufügen
            </Button>
          </Form>

          {/* List of existing valve types */}
          <div>
            <h6>Vorhandene Ventil-Typen:</h6>
            {Object.keys(valveTypes).length === 0 ? (
              <p className="text-muted">Keine Ventil-Typen vorhanden.</p>
            ) : (
              <ListGroup>
                {Object.entries(valveTypes).map(([id, type]) => (
                  <ListGroup.Item key={id} className="d-flex justify-content-between align-items-center">
                    <span>{type}</span>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveValveType(id)}
                    >
                      Löschen
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BikeConfigPage;
